import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { apiClient } from "../../services/api/apiClient";
import { useSelector } from "react-redux";

export default function DuctStepModal({
  isOpen,
  onClose,
  steps = [],
  initialStep = 0,
  editMode = false,
  onFinish,
}) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState({});
  const [notRequiredSteps, setNotRequiredSteps] = useState(new Set());
  const [ductModalData, setDuctModalData] = useState(null);
  const [populatedSteps, setPopulatedSteps] = useState(steps);

  const selectedProjectId = useSelector(
    (state) => state.project.selectedProjectId
  );

  // Transform API data to dropdown options format
  const transformDataToOptions = (
    dataArray,
    labelField = "name",
    valueField = "id"
  ) => {
    if (!dataArray || !Array.isArray(dataArray)) return [];
    return dataArray.map((item) => ({
      label:
        item[labelField] || item.product_type_name || item.name || "Unknown",
      value: item[valueField] || item.id || item.name,
    }));
  };

  // Populate steps with API data
  const populateStepsWithData = (apiData) => {
    if (!apiData || !apiData.data) return steps;

    const data = apiData.data;

    return steps.map((step) => {
      const newStep = { ...step };

      // Update fields based on step title or field names
      if (newStep.fields) {
        newStep.fields = newStep.fields.map((field) => {
          const newField = { ...field };

          // Map different fields to their corresponding API data
          switch (field.name) {
            case "spiral_duct_prod_id":
              newField.options = transformDataToOptions(data.spiral_ducts);
              break;
            case "spiral_ftg_prod_id":
              newField.options = transformDataToOptions(data.spiral_fittings);
              break;
            case "spiral_heto_prod_id":
              newField.options = transformDataToOptions(data.hetos);
              break;
            case "hanger_susp_prod_id":
              newField.options = transformDataToOptions(
                data.hanger_suspension_system
              );
              break;
            case "hanger_inserts_prod_id":
              newField.options = transformDataToOptions(data.hanger_inserts);
              break;
            case "flex_conn_prod_id":
              newField.options = transformDataToOptions(
                data.flexible_connector_material
              );
              break;
            case "flex_duct_type_id":
              newField.options = transformDataToOptions(
                data.flexible_duct_type
              );
              break;
            case "flex_duct_insulation_prod_id":
              newField.options = transformDataToOptions(
                data.flexible_duct_insulation
              );
              break;
            case "flex_duct_clamp_prod_id":
              newField.options = transformDataToOptions(data.flexible_duct_tie);
              break;
            case "duct_clean_prod_id":
              newField.options = transformDataToOptions(
                data.duct_cleaning_protocol
              );
              break;
            case "leak_test_prod_id":
              newField.options = transformDataToOptions(
                data.leak_testing_protocol
              );
              break;
            default:
              // Keep existing options if no mapping found
              break;
          }

          return newField;
        });
      }

      return newStep;
    });
  };

  // Fetch existing step data from backend for specific step
  const fetchExistingStepData = async (projectId, stepNumber) => {
    try {
      console.log(
        `Fetching existing data for project ${projectId}, step ${
          stepNumber + 1
        }`
      );
      const result = await apiClient.get(
        `/duct-modal/existing-data/${projectId}/${stepNumber + 1}`
      );
      console.log(`Existing step ${stepNumber + 1} data fetched:`, result);
      return result;
    } catch (error) {
      console.error(
        `Error fetching existing step ${stepNumber + 1} data:`,
        error
      );
      return null;
    }
  };

  // Display previously selected data for current step
  const displayExistingDataForStep = async (stepNumber) => {
    console.log(`Displaying existing data for step ${stepNumber + 1}`);

    if (!selectedProjectId) {
      console.log("No project selected, skipping data fetch");
      return;
    }

    try {
      const existingData = await fetchExistingStepData(
        selectedProjectId,
        stepNumber
      );

      if (existingData && existingData.data) {
        console.log(
          `Found existing data for step ${stepNumber + 1}:`,
          existingData.data
        );

        // Update form data with existing values
        const updatedFormData = { ...formData };

        // Update spec section and section title for current step
        if (existingData.data.specSection !== undefined) {
          updatedFormData[`step_${stepNumber}_specSection`] =
            existingData.data.specSection;
        }
        if (existingData.data.sectionTitle !== undefined) {
          updatedFormData[`step_${stepNumber}_sectionTitle`] =
            existingData.data.sectionTitle;
        }

        // Update field data for current step
        if (existingData.data.fieldData) {
          Object.keys(existingData.data.fieldData).forEach((fieldName) => {
            updatedFormData[fieldName] = existingData.data.fieldData[fieldName];
          });
        }

        // Handle the "Not Required" toggle based on inc field
        if (existingData.data.inc !== undefined) {
          setNotRequiredSteps((prev) => {
            const newSet = new Set(prev);
            // If inc is false, the step is NOT required (toggle ON)
            // If inc is true, the step IS required (toggle OFF)
            if (existingData.data.inc === false) {
              newSet.add(stepNumber);
            } else {
              newSet.delete(stepNumber);
            }
            return newSet;
          });
        }

        setFormData(updatedFormData);
      } else {
        console.log(`No existing data found for step ${stepNumber + 1}`);
      }
    } catch (error) {
      console.error(
        `Error displaying existing data for step ${stepNumber + 1}:`,
        error
      );
    }
  };

  // Fetch duct modal data
  useEffect(() => {
    const fetchDuctModalData = async () => {
      try {
        const result = await apiClient.get("/duct-modal/data");

        // Store the raw data
        setDuctModalData(result);

        // Populate steps with the fetched data
        const newPopulatedSteps = populateStepsWithData(result);
        setPopulatedSteps(newPopulatedSteps);
      } catch (error) {
        console.error("Error fetching duct modal data:", error);
      }
    };

    if (isOpen) {
      fetchDuctModalData();
    }
  }, [isOpen, steps]);

  // Initialize form data with default values
  const initializeFormWithDefaults = (steps) => {
    const defaultData = {};
    steps.forEach((step, stepIndex) => {
      if (step.defaultSpecSection) {
        defaultData[`step_${stepIndex}_specSection`] = step.defaultSpecSection;
      }
      if (step.defaultSectionTitle) {
        defaultData[`step_${stepIndex}_sectionTitle`] =
          step.defaultSectionTitle;
      }
    });
    return defaultData;
  };

  // Reset modal state when it opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(initialStep);
      // Initialize with default values from step configurations
      const defaultFormData = initializeFormWithDefaults(populatedSteps);
      setFormData(defaultFormData);
      setNotRequiredSteps(new Set());

      // Display existing data for the initial step when modal opens
      if (selectedProjectId && populatedSteps.length > 0) {
        console.log(
          `Modal opened, displaying existing data for step ${initialStep + 1}`
        );
        displayExistingDataForStep(initialStep);
      }
    }
  }, [isOpen, populatedSteps, initialStep]);

  if (!isOpen) return null;

  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleCheckboxChange = (fieldName, value, isChecked) => {
    setFormData((prev) => {
      // Ensure currentValues is always an array
      let currentValues = prev[fieldName] || [];
      if (!Array.isArray(currentValues)) {
        // If it's not an array (e.g., a string from backend), convert it to array
        currentValues = currentValues ? [currentValues] : [];
      }

      if (isChecked) {
        // Add value if checked
        return {
          ...prev,
          [fieldName]: [...currentValues, value],
        };
      } else {
        // Remove value if unchecked
        return {
          ...prev,
          [fieldName]: currentValues.filter((item) => item !== value),
        };
      }
    });
  };

  const handleNotRequiredToggle = () => {
    setNotRequiredSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(currentStep)) {
        newSet.delete(currentStep);
      } else {
        newSet.add(currentStep);
      }
      return newSet;
    });
  };

  const handleNext = async () => {
    // Print current step data before moving to next step
    const currentStepData = populatedSteps[currentStep] || {};
    const stepFieldData = {};

    // Collect data for current step
    if (currentStepData.fields) {
      currentStepData.fields.forEach((field) => {
        stepFieldData[field.name] = formData[field.name] || null;
      });
    }

    // Collect title and spec section for current step
    const stepTitleData = {
      specSection: formData[`step_${currentStep}_specSection`] || null,
      sectionTitle: formData[`step_${currentStep}_sectionTitle`] || null,
    };

    const stepSummary = {
      stepNumber: currentStep + 1,
      stepTitle: currentStepData.title,
      isNotRequired: notRequiredSteps.has(currentStep),
      titleData: stepTitleData,
      fieldData: stepFieldData,
      timestamp: new Date().toISOString(),
    };

    console.log(
      `Step ${currentStep + 1} Data:`,
      JSON.stringify(stepSummary, null, 2)
    );

    // Call backend API to save step data
    try {
      const stepDataToSend = {
        data: stepSummary,
        submittalId: selectedProjectId,
        step: currentStep + 1,
      };

      const response = await apiClient.put("/duct-modal/step", stepDataToSend);
      console.log("Backend response:", response);
    } catch (error) {
      console.error("Error saving step data:", error);
    }

    if (currentStep < populatedSteps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // Display existing data for the next step
      displayExistingDataForStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      // Display existing data for the previous step
      displayExistingDataForStep(prevStep);
    }
  };

  const handleSave = async () => {
    // Save only the current step when in edit mode
    const currentStepData = populatedSteps[currentStep] || {};
    const stepFieldData = {};

    // Collect data for current step
    if (currentStepData.fields) {
      currentStepData.fields.forEach((field) => {
        stepFieldData[field.name] = formData[field.name] || null;
      });
    }

    // Collect title and spec section for current step
    const stepTitleData = {
      specSection: formData[`step_${currentStep}_specSection`] || null,
      sectionTitle: formData[`step_${currentStep}_sectionTitle`] || null,
    };

    const stepSummary = {
      stepNumber: currentStep + 1,
      stepTitle: currentStepData.title,
      isNotRequired: notRequiredSteps.has(currentStep),
      titleData: stepTitleData,
      fieldData: stepFieldData,
      timestamp: new Date().toISOString(),
    };

    console.log(
      `Saving Step ${currentStep + 1} Data:`,
      JSON.stringify(stepSummary, null, 2)
    );

    // Call backend API to save step data
    try {
      const stepDataToSend = {
        data: stepSummary,
        submittalId: selectedProjectId,
        step: currentStep + 1,
      };

      const response = await apiClient.put("/duct-modal/step", stepDataToSend);
      console.log("Backend response:", response);

      // Close modal after successful save
      onClose();
    } catch (error) {
      console.error("Error saving step data:", error);
    }
  };

  const handleFinish = async () => {
    // Print final step data before finishing
    const currentStepData = populatedSteps[currentStep] || {};
    const stepFieldData = {};

    // Collect data for final step
    if (currentStepData.fields) {
      currentStepData.fields.forEach((field) => {
        stepFieldData[field.name] = formData[field.name] || null;
      });
    }

    // Collect title and spec section for final step
    const stepTitleData = {
      specSection: formData[`step_${currentStep}_specSection`] || null,
      sectionTitle: formData[`step_${currentStep}_sectionTitle`] || null,
    };

    const stepSummary = {
      stepNumber: currentStep + 1,
      stepTitle: currentStepData.title,
      isNotRequired: notRequiredSteps.has(currentStep),
      titleData: stepTitleData,
      fieldData: stepFieldData,
      timestamp: new Date().toISOString(),
    };

    console.log(
      `Final Step ${currentStep + 1} Data:`,
      JSON.stringify(stepSummary, null, 2)
    );

    // Call backend API to save final step data
    try {
      const stepDataToSend = {
        data: stepSummary,
        submittalId: selectedProjectId, // Using selected project ID as submittal ID for now
        step: currentStep + 1,
      };

      const response = await apiClient.put("/duct-modal/step", stepDataToSend);
      console.log("Backend response for final step:", response);
    } catch (error) {
      console.error("Error saving final step data:", error);
    }

    const notRequiredArray = Array.from(notRequiredSteps);

    // Separate title data from regular form data
    const titleData = {};
    const regularFormData = {};

    Object.keys(formData).forEach((key) => {
      if (key.includes("_sectionTitle") || key.includes("_specSection")) {
        titleData[key] = formData[key];
      } else {
        regularFormData[key] = formData[key];
      }
    });

    // Create complete step data including null values for not required steps
    const completeStepData = {};
    const completeTitleData = {};

    populatedSteps.forEach((step, stepIndex) => {
      const stepKey = `step${stepIndex + 1}`; // step1, step2, step3, etc.
      const formStepKey = `step_${stepIndex}`; // for form data keys

      // Handle title data for each step
      completeTitleData[`${stepKey}_sectionTitle`] = notRequiredSteps.has(
        stepIndex
      )
        ? null
        : formData[`${formStepKey}_sectionTitle`] || null;

      completeTitleData[`${stepKey}_specSection`] = notRequiredSteps.has(
        stepIndex
      )
        ? null
        : formData[`${formStepKey}_specSection`] || null;

      // Handle field data for each step - group all fields under stepX
      if (step.fields) {
        const stepFieldData = {};
        step.fields.forEach((field) => {
          if (notRequiredSteps.has(stepIndex)) {
            stepFieldData[field.name] = null;
          } else {
            stepFieldData[field.name] = formData[field.name] || null;
          }
        });
        completeStepData[stepKey] = stepFieldData;
      }
    });

    console.log(
      JSON.stringify(
        {
          formData: regularFormData, // Original form data (only filled fields)
          completeStepData, // Complete data with nulls for skipped steps
          titleData: completeTitleData, // Complete title data with nulls
          notRequiredSteps: notRequiredArray,
          stepTitles: notRequiredArray.map(
            (stepIndex) => populatedSteps[stepIndex]?.title
          ),
        },
        null,
        2
      )
    );
    onClose();
    // Call onFinish callback if provided
    if (onFinish) {
      onFinish();
    }
  };

  const currentStepData = populatedSteps[currentStep] || {};
  const isCurrentStepNotRequired = notRequiredSteps.has(currentStep);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3
              className={`text-lg font-semibold ${
                isCurrentStepNotRequired ? "text-gray-400" : "text-gray-900"
              }`}
            >
              {currentStepData.title || `Step ${currentStep + 1}`}
            </h3>
            <p className="text-sm text-gray-500">
              {/* Step {currentStep + 1} of {populatedSteps.length} */}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <span className="text-xs font-medium text-gray-700">
                Not Required
              </span>
              <button
                onClick={handleNotRequiredToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  isCurrentStepNotRequired ? "bg-indigo-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isCurrentStepNotRequired ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                isCurrentStepNotRequired ? "bg-gray-400" : "bg-indigo-600"
              }`}
              style={{
                width: `${((currentStep + 1) / populatedSteps.length) * 100}%`,
              }}
            />
          </div>
        </div>

        {/* Spec Section and Section Title Inputs */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Spec Section
              </label>
              <input
                type="text"
                value={formData[`step_${currentStep}_specSection`] || ""}
                onChange={(e) =>
                  handleInputChange(
                    `step_${currentStep}_specSection`,
                    e.target.value
                  )
                }
                placeholder="Enter spec section"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isCurrentStepNotRequired}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Section Title
              </label>
              <input
                type="text"
                value={formData[`step_${currentStep}_sectionTitle`] || ""}
                onChange={(e) =>
                  handleInputChange(
                    `step_${currentStep}_sectionTitle`,
                    e.target.value
                  )
                }
                placeholder="Enter section title"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isCurrentStepNotRequired}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          className={`px-6 py-4 min-h-[200px] transition-opacity ${
            isCurrentStepNotRequired ? "opacity-30" : "opacity-100"
          }`}
        >
          {currentStepData.description && (
            <p className="text-gray-600 mb-4">{currentStepData.description}</p>
          )}

          {isCurrentStepNotRequired && (
            <div className="flex items-center justify-center min-h-[150px]">
              <p className="text-gray-400 text-sm font-medium">
                This step is marked as not required
              </p>
            </div>
          )}

          {/* Render form fields based on step configuration */}
          {!isCurrentStepNotRequired &&
            currentStepData.fields &&
            currentStepData.fields.map((field, index) => (
              <div key={index} className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.label}
                </label>

                {field.type === "dropdown" ? (
                  <select
                    value={formData[field.name] || ""}
                    onChange={(e) =>
                      handleInputChange(field.name, e.target.value)
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select {field.label}</option>
                    {field.options &&
                      field.options.map((option, optIndex) => (
                        <option key={optIndex} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </select>
                ) : field.type === "radio" ? (
                  <div className="space-y-2">
                    {field.options &&
                      field.options.map((option, optIndex) => (
                        <label
                          key={optIndex}
                          className="flex items-center cursor-pointer"
                        >
                          <input
                            type="radio"
                            name={field.name}
                            value={option.value}
                            checked={formData[field.name] === option.value}
                            onChange={(e) =>
                              handleInputChange(field.name, e.target.value)
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                  </div>
                ) : field.type === "checkbox" ? (
                  <div className="space-y-2">
                    {field.options &&
                      field.options.map((option, optIndex) => (
                        <label
                          key={optIndex}
                          className="flex items-center cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            name={field.name}
                            value={option.value}
                            checked={(() => {
                              // Ensure consistent array handling with handleCheckboxChange
                              let currentValues = formData[field.name] || [];
                              if (!Array.isArray(currentValues)) {
                                currentValues = currentValues
                                  ? [currentValues]
                                  : [];
                              }
                              return currentValues.includes(option.value);
                            })()}
                            onChange={(e) =>
                              handleCheckboxChange(
                                field.name,
                                option.value,
                                e.target.checked
                              )
                            }
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {option.label}
                          </span>
                        </label>
                      ))}
                  </div>
                ) : (
                  <input
                    type={field.type || "text"}
                    value={formData[field.name] || ""}
                    onChange={(e) =>
                      handleInputChange(field.name, e.target.value)
                    }
                    placeholder={field.placeholder}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>
            ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t">
          {editMode ? (
            // Edit mode: Show Cancel and Save buttons
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>

              <span className="text-sm text-gray-500">
                Editing: {currentStepData.title || `Step ${currentStep + 1}`}
              </span>

              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
              >
                Save
              </button>
            </>
          ) : (
            // Normal mode: Show Previous, Next, and Finish buttons
            <>
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="w-4 h-4 mr-1" />
                Previous
              </button>

              <span className="text-sm text-gray-500">
                Step {currentStep + 1} of {populatedSteps.length}
              </span>

              {currentStep === populatedSteps.length - 1 ? (
                <button
                  onClick={handleFinish}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                >
                  Finish
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                >
                  Next
                  <ChevronRightIcon className="w-4 h-4 ml-1" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
